import { isHidden, isSkipped, selectContentRoot, walkVisible } from './contentRoot';
import { serializeControls } from './controls';
import { precedingText } from './descriptors';
import { serializeTextBlocks, visibleText } from './textBlocks';
import { StyleView } from './types';

export type OverlaySections = { dialog: string; notifications: string; nonText: string };

const NON_TEXT_TAGS = new Set(['CANVAS', 'IFRAME', 'OBJECT', 'EMBED']);

const SEVERITY_PATTERNS = [
  /MuiAlert-(?:standard|filled|outlined)(Success|Info|Warning|Error)/,
  /variant(Success|Info|Warning|Error)/,
  /notistack-MuiContent-(success|info|warning|error)/,
];

const isExcluded = (el: Element): boolean => el.closest('[data-screen-exclude]') !== null;

// An overlay is visible when neither it nor its MUI modal root is hidden (closed `keepMounted` dialogs stay in the DOM).
const isOverlayVisible = (el: Element, view: StyleView): boolean => {
  const modalRoot = el.closest('.MuiDialog-root');
  return !isHidden(el, view) && !(modalRoot && isHidden(modalRoot, view));
};

// Keeps the outermost matches only, so a dialog nested in another dialog is not reported twice.
const outermost = (elements: Element[]): Element[] => elements.filter(
  (el) => !elements.some((other) => other !== el && other.contains(el)),
);

export const findOpenDialogs = (doc: Document, view: StyleView): Element[] => {
  const papers = Array.from(doc.querySelectorAll('[role="dialog"]'));
  // A MUI Dialog whose Paper lost `role="dialog"` (custom PaperProps) is still caught through its root.
  const rootsWithoutRole = Array.from(doc.querySelectorAll('.MuiDialog-root'))
    .filter((root) => root.querySelector('[role="dialog"]') === null);

  return outermost([...papers, ...rootsWithoutRole])
    .filter((el) => !isExcluded(el) && isOverlayVisible(el, view));
};

const dialogTitle = (dialog: Element, view: StyleView): string => {
  const ariaLabel = dialog.getAttribute('aria-label');
  if (ariaLabel) return ariaLabel.trim();

  const labelledBy = (dialog.getAttribute('aria-labelledby') ?? '').split(/\s+/).filter(Boolean)
    .map((id) => dialog.ownerDocument.getElementById(id))
    .filter((ref): ref is HTMLElement => ref !== null)
    .map((ref) => visibleText(ref, view))
    .join(' ');
  if (labelledBy) return labelledBy;

  const titleEl = dialog.querySelector('.MuiDialogTitle-root');
  return titleEl ? visibleText(titleEl, view) : '';
};

const serializeDialog = (dialog: Element, view: StyleView): string => {
  const title = dialogTitle(dialog, view);
  const text = serializeTextBlocks(dialog, view)
    .split('\n')
    .filter((line) => line.replace(/^#+\s*/, '') !== title)
    .join('\n');
  const { inputs, actions } = serializeControls(dialog, view);

  return [
    `### Dialog ${title ? `"${title}"` : '(untitled)'}`,
    text,
    inputs ? `Inputs:\n${inputs}` : '',
    actions ? `Actions:\n${actions}` : '',
  ].filter(Boolean).join('\n');
};

const severityOf = (alert: Element): string => {
  const className = [alert, ...Array.from(alert.querySelectorAll('[class]'))]
    .map((el) => el.getAttribute('class') ?? '')
    .join(' ');
  const severity = SEVERITY_PATTERNS
    .map((pattern) => className.match(pattern)?.[1])
    .find(Boolean);
  return severity ? `[${severity.toLowerCase()}] ` : '';
};

// Positive allow-list of toast containers: notistack's public container class and MUI Snackbar roots.
// A missed toast is harmless, whereas matching every `role="alert"` would leak alerts from the sidebar,
// order tabs, information bar or assistant panel.
const TOAST_ALERT_SELECTOR = '.notistack-SnackbarContainer [role="alert"], .MuiSnackbar-root [role="alert"]';

/**
 * Toasts render inline rather than in a body portal. Alerts inside the content root or an open dialog
 * are already part of those sections. The header's real-time Snackbar is marked `data-screen-exclude`
 * because it can name other orders.
 */
const serializeNotifications = (doc: Document, view: StyleView, root: Element, dialogs: Element[]): string => {
  const alerts = Array.from(doc.querySelectorAll(TOAST_ALERT_SELECTOR))
    .filter((alert) => !root.contains(alert))
    .filter((alert) => !dialogs.some((dialog) => dialog.contains(alert)))
    .filter((alert) => !isExcluded(alert) && !isSkipped(alert, view));

  return outermost(alerts)
    .map((alert) => ({ alert, text: visibleText(alert, view) }))
    .filter(({ text }) => text !== '')
    .map(({ alert, text }) => `- ${severityOf(alert)}${text}`)
    .join('\n');
};

const nonTextLabel = (el: Element, view: StyleView): string => (
  el.getAttribute('aria-label')
  || el.getAttribute('title')
  || precedingText(el, view)
  || '(unlabeled)'
).trim();

// PDF viewers draw one canvas per page; identical entries collapse into one line with a count.
const collapseRepeats = (lines: string[]): string[] => {
  const counts = lines.reduce((acc, line) => acc.set(line, (acc.get(line) ?? 0) + 1), new Map<string, number>());
  return Array.from(counts.entries()).map(([line, count]) => (count > 1 ? `${line} ×${count}` : line));
};

const serializeNonText = (scopes: Element[], view: StyleView): string => {
  const lines: string[] = [];
  scopes.forEach((scope) => walkVisible(scope, view, (el) => {
    if (NON_TEXT_TAGS.has(el.tagName)) lines.push(`- ${el.tagName.toLowerCase()} "${nonTextLabel(el, view)}"`);
  }));
  return collapseRepeats(lines).join('\n');
};

/** Serializes what floats above the screen: open dialogs, toast notifications and non-text content. */
export const serializeOverlays = (doc: Document, view: StyleView): OverlaySections => {
  const root = selectContentRoot(doc);
  const dialogs = findOpenDialogs(doc, view);

  return {
    dialog: dialogs.map((dialog) => serializeDialog(dialog, view)).join('\n\n'),
    notifications: serializeNotifications(doc, view, root, dialogs),
    nonText: serializeNonText([root, ...dialogs.filter((dialog) => !root.contains(dialog))], view),
  };
};
